import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { ApiError } from '@/api/errors'
import type { CreateWebPairingResponse, WebPairingStatusResponse } from '@/api/pending'
import { telemetry } from '@/app/telemetry'
import { sessionStore } from './sessionStore'

export const POLL_INTERVAL_MS = 2000
/** Codes are regenerated on expiry; stop after this many in a row so an idle tab doesn't hammer the API. */
const MAX_AUTO_REGENERATIONS = 5

export type PairingState =
  | { phase: 'creating' }
  | { phase: 'waiting'; pairing: CreateWebPairingResponse; expiresAt: number }
  | { phase: 'denied' }
  | { phase: 'expired' }
  | { phase: 'no_access' }
  | { phase: 'error' }

export function usePairing(onApproved: () => void, pollMs: number = POLL_INTERVAL_MS) {
  const [state, setState] = useState<PairingState>({ phase: 'creating' })
  const [generation, setGeneration] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const autoCount = useRef(0)
  const onApprovedRef = useRef(onApproved)
  useEffect(() => {
    onApprovedRef.current = onApproved
  })

  const regenerate = useCallback(() => {
    setState({ phase: 'creating' })
    setGeneration((g) => g + 1)
  }, [])

  /** Expiry while the user is away: silently get a fresh code, up to a limit. */
  const autoRegenerate = useCallback(() => {
    telemetry.breadcrumb('pairing', 'expired')
    if (autoCount.current >= MAX_AUTO_REGENERATIONS) {
      setState({ phase: 'expired' })
      return
    }
    autoCount.current += 1
    regenerate()
  }, [regenerate])

  /** User-initiated retry (denied / expired / error). */
  const retry = useCallback(() => {
    autoCount.current = 0
    regenerate()
  }, [regenerate])

  // Create a pairing (on mount and whenever `generation` changes).
  useEffect(() => {
    let cancelled = false
    api
      .post<CreateWebPairingResponse>(
        '/auth/web-pairings',
        { user_agent: navigator.userAgent },
        { auth: false },
      )
      .then((pairing) => {
        if (cancelled) return
        telemetry.breadcrumb('pairing', 'created')
        setNow(Date.now())
        setState({ phase: 'waiting', pairing, expiresAt: Date.parse(pairing.expires_at) })
      })
      .catch(() => {
        if (!cancelled) setState({ phase: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [generation])

  // Countdown tick; reaching zero regenerates.
  const expiresAt = state.phase === 'waiting' ? state.expiresAt : null
  useEffect(() => {
    if (expiresAt === null) return
    const id = window.setInterval(() => {
      const t = Date.now()
      setNow(t)
      if (t >= expiresAt) autoRegenerate()
    }, 1000)
    return () => window.clearInterval(id)
  }, [expiresAt, autoRegenerate])

  // Poll the pairing status every 2s.
  const pairing = state.phase === 'waiting' ? state.pairing : null
  useEffect(() => {
    if (!pairing) return
    let cancelled = false
    let inFlight = false
    const id = window.setInterval(async () => {
      if (inFlight) return
      inFlight = true
      try {
        const res = await api.get<WebPairingStatusResponse>('/auth/web-pairings/:id', {
          params: { id: pairing.pairing_id },
          headers: { 'X-Pairing-Secret': pairing.poll_secret },
          auth: false,
        })
        if (cancelled) return
        if (res.status === 'approved' && res.access_token && res.refresh_token) {
          telemetry.breadcrumb('pairing', 'approved')
          sessionStore.setTokens({
            access_token: res.access_token,
            refresh_token: res.refresh_token,
          })
          onApprovedRef.current()
        } else if (res.status === 'denied') {
          telemetry.breadcrumb('pairing', 'denied', { reason: res.reason })
          setState({ phase: res.reason === 'no_manager_access' ? 'no_access' : 'denied' })
        } else if (res.status === 'expired') {
          autoRegenerate()
        }
      } catch (e) {
        // 404: expired, already consumed or wrong secret. Anything else is transient: keep polling.
        if (!cancelled && e instanceof ApiError && e.status === 404) autoRegenerate()
      } finally {
        inFlight = false
      }
    }, pollMs)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [pairing, autoRegenerate, pollMs])

  const secondsLeft = expiresAt === null ? 0 : Math.max(0, Math.ceil((expiresAt - now) / 1000))
  return { state, secondsLeft, retry }
}
